/**
 * Display asset name for a tenant row (nested asset, flat field, or resolve via `asset_id`
 * against deal assets — API tenants often omit nested `asset`).
 */
export function getDisplayAssetNameForDealTenant(
  tenant: any,
  assets?: any[],
): string {
  const direct = tenant?.asset?.asset_name ?? tenant?.asset_name;
  if (typeof direct === "string" && direct.trim() !== "") {
    return direct.trim();
  }
  const aid = tenant?.asset_id ?? tenant?.asset?.id;
  if (assets?.length && aid != null && `${aid}` !== "") {
    const row = assets.find(
      (a) =>
        a &&
        (a.id === aid ||
          (typeof a.id === typeof aid && String(a.id) === String(aid))),
    );
    if (typeof row?.asset_name === "string" && row.asset_name.trim() !== "") {
      return row.asset_name.trim();
    }
  }
  return "";
}

/**
 * Validates a typed asset name against deal assets (trimmed exact match on asset_name).
 * Empty / whitespace-only is valid.
 */
export function validateDealTenantAssetNameValue(
  value: unknown,
  assets: any[],
): string[] | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const found = assets.some((a) => {
    const n = typeof a?.asset_name === "string" ? a.asset_name.trim() : "";
    return n === trimmed;
  });
  if (!found) {
    return ["asset name must match an asset found in the assets table"];
  }
  return null;
}

/** True if the tenant row has a non-empty asset name that does not match any deal asset. */
export function dealTenantRowHasAssetNameError(
  tenant: any,
  assets: any[],
): boolean {
  const msgs = validateDealTenantAssetNameValue(
    getDisplayAssetNameForDealTenant(tenant, assets),
    assets,
  );
  return msgs != null && msgs.length > 0;
}

/** True if any tenant in the list has an invalid asset_name for the current deal assets. */
export function hasAnyDealTenantAssetNameError(
  tenants: any[] | undefined,
  assets: any[] | undefined,
): boolean {
  if (!tenants?.length) return false;
  const list = assets ?? [];
  return tenants.some((t) => dealTenantRowHasAssetNameError(t, list));
}

/** UI-only tenant flags must not be sent; the API constructs Tenant(**payload). */
function tenantPayloadForAssetUpdate(tenant: any, options?: { clearId?: boolean }) {
  const item = { ...tenant };
  if (options?.clearId) item.id = null;
  delete item.shouldCreate;
  delete item.included;
  delete item.shouldLink;
  delete item.prev_asset;
  delete item.prev_asset_id;
  delete item.asset_name;
  delete item.asset;
  return item;
}

/** Tenants body for PUT asset when syncing by asset_name after deal save. */
export function buildTenantsForAssetUpdate(
  assetName: string,
  tenants: any[],
  assets: any[],
): any[] {
  const trimmed = assetName.trim();
  const assetRow = assets.find(
    (a) =>
      typeof a?.asset_name === "string" && a.asset_name.trim() === trimmed,
  );
  const assetId = assetRow?.id;

  const tenantsToSend: any[] = [];
  for (const t of tenants) {
    const resolvedName = getDisplayAssetNameForDealTenant(t, assets);
    const nameMatch = resolvedName === trimmed;
    const idMatch =
      assetId != null &&
      (t.asset_id === assetId ||
        t.asset?.id === assetId ||
        String(t.asset_id) === String(assetId));

    if (!nameMatch && !idMatch) continue;

    if (t.id != null && typeof t.id === "number") {
      tenantsToSend.push(tenantPayloadForAssetUpdate(t));
    } else if (t?.asset_name === trimmed || nameMatch || idMatch) {
      tenantsToSend.push(tenantPayloadForAssetUpdate(t, { clearId: true }));
    }
  }
  return tenantsToSend;
}
